// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {joinDiscoveryGuild} from '@app/features/discovery/commands/DiscoveryJoinCommands';
import {resolveBannerTintClassName} from '@app/features/discovery/utils/DiscoveryBannerTint';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import Guilds from '@app/features/guild/state/Guilds';
import * as GuildTagCommands from '@app/features/guild_tag/commands/GuildTagCommands';
import styles from '@app/features/guild_tag/components/GuildTagCard.module.css';
import {JOIN_COMMUNITY_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {Button} from '@app/features/ui/button/Button';
import {getGuildBannerURL} from '@app/features/user/utils/AvatarUtils';
import type {GuildTagCardResponse} from '@fluxer/schema/src/domains/guild/GuildTagSchemas';
import {extractTimestamp} from '@fluxer/snowflake/src/SnowflakeUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

const GO_TO_COMMUNITY_DESCRIPTOR = msg({
	message: 'Go to community',
	comment: 'Button in the card opened from a server tag, for a member of that community.',
});
const MEMBERS_DESCRIPTOR = msg({
	message: '{count, plural, one {# member} other {# members}}',
	comment: 'Member count of a community in the card opened from a server tag.',
});
const ONLINE_DESCRIPTOR = msg({
	message: '{count, plural, one {# online} other {# online}}',
	comment: 'Approximate number of online members of a community in the card opened from a server tag.',
});
const CREATED_DESCRIPTOR = msg({
	message: 'Created: {date}',
	comment: 'When the community was created, in the card opened from a server tag. {date} is a month and year.',
});
const MORE_TAGS_DESCRIPTOR = msg({
	message: '{count, plural, one {+# more} other {+# more}}',
	comment:
		'Pill after the first tags of a community in the card opened from a server tag: how many tags are not shown.',
});
const PRIVATE_COMMUNITY_DESCRIPTOR = msg({
	message: 'Private community',
	comment: 'Title of the card opened from a server tag when the community is not listed in discovery.',
});
const PRIVATE_COMMUNITY_HINT_DESCRIPTOR = msg({
	message: 'The details of this community are not public.',
	comment: 'Line under "Private community" in the card opened from a server tag.',
});
const CARD_FAILED_DESCRIPTOR = msg({
	message: 'Could not load this community. Open the tag again to retry.',
	comment: 'Shown in the card opened from a server tag when reading the community failed.',
});

const GUILD_ICON_SIZE_PX = 64;
const SHOWN_TAG_COUNT = 3;

type RemoteCard = {status: 'loading'} | {status: 'failed'} | {status: 'done'; card: GuildTagCardResponse | null};

// Read once the card opens, so a tag on a row costs no request. A member reads it too, for the listing text.
function useRemoteCard(guildId: string): RemoteCard {
	const [remote, setRemote] = useState<RemoteCard>({status: 'loading'});
	useEffect(() => {
		let current = true;
		GuildTagCommands.fetchCard(guildId).then(
			(card) => {
				if (current) setRemote({status: 'done', card});
			},
			() => {
				if (current) setRemote({status: 'failed'});
			},
		);
		return () => {
			current = false;
		};
	}, [guildId]);
	return remote;
}

interface GuildTagCardProps {
	guildId: string;
	tag: string;
	chip: React.ReactNode;
	onClose: () => void;
}

export const GuildTagCard = observer(function GuildTagCard({guildId, tag, chip, onClose}: GuildTagCardProps) {
	const {i18n} = useLingui();
	const member = Guilds.getGuild(guildId);
	const remote = useRemoteCard(guildId);
	const [joining, setJoining] = useState(false);
	const card = remote.status === 'done' ? remote.card : null;
	const shown = member ?? card;
	const banner = card?.banner ?? member?.banner ?? null;
	const memberCount = card?.member_count ?? member?.memberCount;
	const tags = card?.custom_tags ?? [];
	const handleGo = () => {
		NavigationCommands.selectGuild(guildId);
		onClose();
	};
	const handleJoin = async () => {
		setJoining(true);
		const joined = await joinDiscoveryGuild(guildId);
		setJoining(false);
		if (joined) onClose();
	};
	let body: React.ReactNode;
	if (shown) {
		body = (
			<>
				<div className={styles.banner} aria-hidden="true" data-flx="guild-tag.guild-tag-card.banner">
					{banner ? (
						<img
							src={getGuildBannerURL({id: guildId, banner})}
							alt=""
							className={styles.bannerImage}
							data-flx="guild-tag.guild-tag-card.banner-image"
						/>
					) : (
						<span className={resolveBannerTintClassName(guildId)} />
					)}
				</div>
				<GuildIcon
					id={guildId}
					name={shown.name}
					icon={shown.icon ?? null}
					sizePx={GUILD_ICON_SIZE_PX}
					className={styles.icon}
				/>
				<div className={styles.profile} data-flx="guild-tag.guild-tag-card.text">
					<div className={styles.titleRow}>
						<span className={styles.title} data-flx="guild-tag.guild-tag-card.name">
							{shown.name}
						</span>
						{chip}
					</div>
					<div className={styles.detail} data-flx="guild-tag.guild-tag-card.detail">
						{card?.online_count != null && (
							<span className={styles.stat} data-flx="guild-tag.guild-tag-card.online">
								<span className={styles.onlineDot} aria-hidden="true" />
								{i18n._(ONLINE_DESCRIPTOR, {count: card.online_count})}
							</span>
						)}
						{memberCount ? (
							<span className={styles.stat} data-flx="guild-tag.guild-tag-card.members">
								<span className={styles.memberDot} aria-hidden="true" />
								{i18n._(MEMBERS_DESCRIPTOR, {count: memberCount})}
							</span>
						) : null}
					</div>
					<span className={styles.hint} data-flx="guild-tag.guild-tag-card.created">
						{i18n._(CREATED_DESCRIPTOR, {
							date: i18n.date(new Date(extractTimestamp(guildId)), {month: 'short', year: 'numeric'}),
						})}
					</span>
					{card?.description && (
						<p className={styles.description} data-flx="guild-tag.guild-tag-card.description">
							{card.description}
						</p>
					)}
					{tags.length > 0 && (
						<ul className={styles.tags} data-flx="guild-tag.guild-tag-card.tags">
							{tags.slice(0, SHOWN_TAG_COUNT).map((tag) => (
								<li key={tag} className={styles.tag} data-flx="guild-tag.guild-tag-card.tag">
									{tag}
								</li>
							))}
							{tags.length > SHOWN_TAG_COUNT && (
								<li className={styles.tag} data-flx="guild-tag.guild-tag-card.more-tags">
									{i18n._(MORE_TAGS_DESCRIPTOR, {count: tags.length - SHOWN_TAG_COUNT})}
								</li>
							)}
						</ul>
					)}
				</div>
			</>
		);
	} else if (remote.status === 'loading') {
		body = (
			<div className={styles.head} aria-hidden="true" data-flx="guild-tag.guild-tag-card.loading">
				<span className={styles.iconPlaceholder} />
				<div className={styles.text}>
					<span className={styles.titlePlaceholder} />
					{chip}
				</div>
			</div>
		);
	} else {
		const failed = remote.status === 'failed';
		body = (
			<div className={styles.head} data-flx={`guild-tag.guild-tag-card.${failed ? 'failed' : 'private'}`}>
				{chip}
				<div className={styles.text}>
					{failed ? (
						<span className={styles.hint}>{i18n._(CARD_FAILED_DESCRIPTOR)}</span>
					) : (
						<>
							<span className={styles.title}>{i18n._(PRIVATE_COMMUNITY_DESCRIPTOR)}</span>
							<span className={styles.hint}>{i18n._(PRIVATE_COMMUNITY_HINT_DESCRIPTOR)}</span>
						</>
					)}
				</div>
			</div>
		);
	}
	return (
		<div
			className={shown ? styles.profileCard : styles.card}
			role="dialog"
			aria-label={tag}
			tabIndex={-1}
			data-autofocus
			data-flx="guild-tag.guild-tag-card.card"
		>
			{body}
			{(member || (card?.joinable && !RuntimeConfig.singleCommunityEnabled)) && (
				<div className={shown ? styles.actions : undefined}>
					{member ? (
						<Button fitContainer small onClick={handleGo} data-flx="guild-tag.guild-tag-card.go">
							{i18n._(GO_TO_COMMUNITY_DESCRIPTOR)}
						</Button>
					) : (
						<Button
							fitContainer
							small
							submitting={joining}
							onClick={handleJoin}
							data-flx="guild-tag.guild-tag-card.join"
						>
							{i18n._(JOIN_COMMUNITY_DESCRIPTOR)}
						</Button>
					)}
				</div>
			)}
		</div>
	);
});
